// Analytics service for tracking game events
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export interface GameEvent {
  event: string;
  parameters?: Record<string, any>;
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  initialize(measurementId: string): void {
    if (this.isInitialized) return;
    
    // Initialize GA4
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', measurementId);
      this.isInitialized = true;
    }
  }

  // Track page views
  trackPageView(page: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: page,
        page_location: window.location.href
      });
    }
  }

  // Track game start
  trackGameStart(date: string, caseId?: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'game_start', {
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track clue reveal
  trackClueReveal(clueNumber: number, date: string, caseId?: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'clue_reveal', {
        clue_number: clueNumber,
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track proposal submission
  trackProposalSubmission(
    isCorrect: boolean, 
    correctCount: number, 
    date: string, 
    caseId?: string
  ): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'proposal_submission', {
        is_correct: isCorrect,
        correct_count: correctCount,
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track game completion
  trackGameCompletion(
    moves: number, 
    par: number, 
    date: string, 
    caseId?: string
  ): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'game_completion', {
        moves: moves,
        par: par,
        performance_ratio: moves / par,
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track solution reveal
  trackSolutionReveal(date: string, caseId?: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'solution_reveal', {
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track user interaction
  trackUserInteraction(action: string, details?: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'user_interaction', {
        action: action,
        ...details,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track daily active users
  trackDailyActive(): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'daily_active_user', {
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track case solved (successful submission)
  trackCaseSolved(
    moves: number, 
    par: number, 
    date: string, 
    caseId?: string,
    gameMode?: string
  ): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'case_solved', {
        moves: moves,
        par: par,
        performance_ratio: moves / par,
        date: date,
        case_id: caseId,
        game_mode: gameMode,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track case failed (unsuccessful submission)
  trackCaseFailed(
    date: string, 
    caseId?: string,
    gameMode?: string
  ): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'case_failed', {
        date: date,
        case_id: caseId,
        game_mode: gameMode,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Track share action
  trackShare(
    shareType: 'case_solved' | 'generic',
    date?: string, 
    caseId?: string
  ): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'shared', {
        share_type: shareType,
        date: date,
        case_id: caseId,
        timestamp: new Date().toISOString()
      });
    }
  }

  trackBookPageView(): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'book_page_view', {
        timestamp: new Date().toISOString()
      });
    }
  }

  trackBookAmazonClick(location: 'hero' | 'details'): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'book_amazon_click', {
        location,
        timestamp: new Date().toISOString()
      });
    }
  }
}

export const analytics = AnalyticsService.getInstance(); 